import { useCallback, useEffect, useRef, useState } from "react";

import { useMatrix } from "~/context/MatrixContext";
import {
  MOBILE_SECTION_ORDER,
  moveSection,
  normaliseSidebarLayout,
  setRoomHidden,
  subscribeSidebarLayout,
  writeSidebarLayout,
  withSectionOrder,
  type CoreSectionId,
  type SidebarLayout,
  type SidebarSectionId,
} from "~/lib/sidebar-layout";

export interface UseSidebarLayoutResult {
  layout: SidebarLayout;
  moveSection: (id: CoreSectionId, step: -1 | 1) => void;
  /** Stores `visible` as the order, keeping saved sections this surface doesn't show. */
  setOrder: (visible: readonly SidebarSectionId[]) => void;
  /** Back to each surface's default order. */
  resetOrder: () => void;
  setHidden: (roomId: string, hidden: boolean) => void;
}

/** `defaultOrder` applies until the user reorders; it must be a stable (module-level) array. */
export function useSidebarLayout(
  defaultOrder: readonly CoreSectionId[] = MOBILE_SECTION_ORDER,
): UseSidebarLayoutResult {
  const { client } = useMatrix();
  const [layout, setLayout] = useState<SidebarLayout>(() =>
    normaliseSidebarLayout(null, defaultOrder),
  );
  const layoutRef = useRef(layout);
  layoutRef.current = layout;

  useEffect(() => {
    if (!client) {
      setLayout(normaliseSidebarLayout(null, defaultOrder));
      return;
    }
    return subscribeSidebarLayout(client, setLayout, defaultOrder);
  }, [client, defaultOrder]);

  const commit = useCallback(
    (next: SidebarLayout) => {
      const previous = layoutRef.current;
      if (next === previous) return;
      setLayout(normaliseSidebarLayout(next, defaultOrder));
      if (!client) return;
      writeSidebarLayout(client, next).catch((err) => {
        console.warn("writeSidebarLayout failed", err);
        setLayout(previous);
      });
    },
    [client, defaultOrder],
  );

  return {
    layout,
    moveSection: useCallback(
      (id: CoreSectionId, step: -1 | 1) => commit(moveSection(layoutRef.current, id, step)),
      [commit],
    ),
    setOrder: useCallback(
      (visible: readonly SidebarSectionId[]) =>
        commit(withSectionOrder(layoutRef.current, visible)),
      [commit],
    ),
    resetOrder: useCallback(() => commit({ ...layoutRef.current, order: [] }), [commit]),
    setHidden: useCallback(
      (roomId: string, hidden: boolean) => commit(setRoomHidden(layoutRef.current, roomId, hidden)),
      [commit],
    ),
  };
}
